/**
 * AdminPhotoImage Component
 * 
 * Wrapper component for displaying photos in Admin panels.
 * Accepts relative paths (v1.5 format) and generates signed URLs on demand.
 * Handles loading states and fallbacks gracefully.
 */

import React from 'react';
import { Image as ImageIcon, Loader2 } from 'lucide-react';
import { useImageUrl } from '@/hooks/useImageUrl';
import type { ImageContext } from '@/services/ImageUrlService';

interface AdminPhotoImageProps {
  /** Relative path or URL to the photo */
  src: string | null | undefined;
  /** Alt text for accessibility */
  alt: string;
  /** Display context for variant selection */
  context?: ImageContext;
  /** Additional CSS classes */
  className?: string;
  /** Callback when image loads successfully */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: () => void;
  /** Show loading spinner while fetching signed URL */
  showSpinner?: boolean;
}

const AdminPhotoImage: React.FC<AdminPhotoImageProps> = ({
  src,
  alt,
  context = 'thumbnail',
  className = '',
  onLoad,
  onError,
  showSpinner = true,
}) => {
  const { url: signedUrl, isLoading } = useImageUrl(src || '', context);
  const [hasError, setHasError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  // Reset error state when src changes
  React.useEffect(() => {
    setHasError(false);
    setImageLoaded(false);
  }, [src]);

  const handleLoad = () => {
    setImageLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  // No src provided
  if (!src) {
    return (
      <div className={`${className} flex items-center justify-center bg-white/5 border border-white/10 rounded-lg`}>
        <ImageIcon className="h-6 w-6 text-white/40" />
      </div>
    );
  }

  // Loading state
  if (isLoading && showSpinner) {
    return (
      <div className={`${className} flex items-center justify-center bg-white/5 border border-white/10 rounded-lg`}>
        <Loader2 className="h-6 w-6 text-luxury-pinkBright animate-spin" />
      </div>
    );
  }

  // Error state
  if (hasError || !signedUrl) {
    return (
      <div className={`${className} flex items-center justify-center bg-white/5 border border-white/10 rounded-lg`}>
        <ImageIcon className="h-6 w-6 text-white/40" />
      </div>
    );
  }

  return (
    <div className={`${className} relative overflow-hidden bg-white/5 border border-white/10 rounded-lg`}>
      {/* Loading shimmer overlay */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-white/5 animate-pulse" />
      )}
      
      <img
        src={signedUrl}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

export default AdminPhotoImage;
